import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../components/auth/AuthContext';

const MIN_LENGTH = 8;

const Account = () => {
  const { user, changePassword, setupMfa, verifyMfaSetup, disableMfa, regenerateRecoveryCodes } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!current || !next || !confirm) {
      setError('All fields are required.');
      return;
    }
    if (next.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setError('New password must differ from the current password.');
      return;
    }

    setSubmitting(true);
    const result = await changePassword(current, next);
    setSubmitting(false);

    if (result.success) {
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-semibold text-gray-100 mb-2">Account</h1>
      <div className="text-sm text-gray-400 mb-6">
        Signed in as <span className="text-gray-200">{user?.username}</span>
        {user?.role ? <span className="text-gray-500"> ({user.role})</span> : null}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-100 mb-1">Change password</h2>
        <p className="text-sm text-gray-500 mb-5">
          Changing your password will sign out any other devices where you are
          logged in. This tab will stay signed in.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="current" className="block text-sm font-medium text-gray-300 mb-1">
              Current password
            </label>
            <input
              id="current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="next" className="block text-sm font-medium text-gray-300 mb-1">
              New password
            </label>
            <input
              id="next"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
            />
            <div className="text-xs text-gray-500 mt-1">
              At least {MIN_LENGTH} characters. Must differ from your current password.
            </div>
          </div>

          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-gray-300 mb-1">
              Confirm new password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="bg-red-900/40 border border-red-800 text-red-200 text-sm rounded px-3 py-2">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-900/40 border border-green-800 text-green-200 text-sm rounded px-3 py-2">
              Password updated. Other devices have been signed out.
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-400 text-white font-medium rounded px-4 py-2 transition-colors"
          >
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>

      <div className="mt-6">
        <MfaSection
          user={user}
          setupMfa={setupMfa}
          verifyMfaSetup={verifyMfaSetup}
          disableMfa={disableMfa}
          regenerateRecoveryCodes={regenerateRecoveryCodes}
        />
      </div>
    </div>
  );
};

// Show-once recovery code list with copy + download buttons. Used both
// after first enrolment and after regenerating the codes.
const RecoveryCodesPanel = ({ codes, onAcknowledge }) => {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard API requires user gesture / secure context — fallback
      // to selectable text-area; user can manually Cmd-C.
    }
  };

  const download = () => {
    const blob = new Blob([
      `AI Sentry — recovery codes\nGenerated: ${new Date().toISOString()}\n\n`,
      text,
      '\n\nKeep these somewhere safe. Each code can only be used once.\n',
    ], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-sentry-recovery-codes-${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-amber-900/30 border border-amber-700 rounded p-4">
      <div className="text-amber-200 text-sm font-medium mb-2">
        Save these recovery codes now — you won't see them again.
      </div>
      <div className="text-xs text-amber-300/80 mb-4">
        If you lose access to your authenticator app, each code lets you sign in once.
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {codes.map((code) => (
          <code key={code} className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-gray-100 font-mono text-sm text-center">
            {code}
          </code>
        ))}
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={copy}
          className="flex-1 border border-gray-600 hover:bg-gray-800 text-gray-200 font-medium rounded px-3 py-2 text-sm"
        >
          {copied ? 'Copied!' : 'Copy all'}
        </button>
        <button
          type="button"
          onClick={download}
          className="flex-1 border border-gray-600 hover:bg-gray-800 text-gray-200 font-medium rounded px-3 py-2 text-sm"
        >
          Download .txt
        </button>
        <button
          type="button"
          onClick={onAcknowledge}
          className="flex-1 bg-amber-700 hover:bg-amber-600 text-white font-medium rounded px-3 py-2 text-sm"
        >
          I've saved them
        </button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// MFA section. Three states: not enrolled (button to start), mid-enrolment
// (QR + secret + verification input), enrolled (status + disable button).
// ──────────────────────────────────────────────────────────────────────────

const MfaSection = ({ user, setupMfa, verifyMfaSetup, disableMfa, regenerateRecoveryCodes }) => {
  const [enrolment, setEnrolment] = useState(null); // {secret, provisioningUrl}
  const [verifyCode, setVerifyCode] = useState('');
  const [disableForm, setDisableForm] = useState(null); // {password, code}
  // Show-once recovery codes — set by verify-setup or regenerate flow.
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [regenForm, setRegenForm] = useState(null); // {password, code}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  const enabled = !!user?.mfa_enabled;

  const startEnrol = async () => {
    setErr(null); setOk(null); setBusy(true);
    const r = await setupMfa();
    setBusy(false);
    if (!r.success) { setErr(r.error); return; }
    setEnrolment({ secret: r.secret, provisioningUrl: r.provisioningUrl });
    setVerifyCode('');
  };

  const cancelEnrol = () => {
    setEnrolment(null);
    setVerifyCode('');
    setErr(null);
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setErr(null); setOk(null);
    if (verifyCode.length !== 6) { setErr('Enter the 6-digit code from your authenticator.'); return; }
    setBusy(true);
    const r = await verifyMfaSetup(verifyCode);
    setBusy(false);
    if (!r.success) { setErr(r.error); return; }
    setEnrolment(null);
    setVerifyCode('');
    setOk('Two-factor authentication is now enabled.');
    if (r.recoveryCodes && r.recoveryCodes.length) {
      setRecoveryCodes(r.recoveryCodes);
    }
  };

  const startRegen = () => {
    setRegenForm({ password: '', code: '' });
    setErr(null); setOk(null);
  };

  const cancelRegen = () => {
    setRegenForm(null);
    setErr(null);
  };

  const submitRegen = async (e) => {
    e.preventDefault();
    setErr(null); setOk(null);
    if (!regenForm.password || regenForm.code.length !== 6) {
      setErr('Password and a current 6-digit code are both required.');
      return;
    }
    setBusy(true);
    const r = await regenerateRecoveryCodes(regenForm.password, regenForm.code);
    setBusy(false);
    if (!r.success) { setErr(r.error); return; }
    setRegenForm(null);
    setOk('New recovery codes issued. Old codes are no longer valid.');
    if (r.recoveryCodes && r.recoveryCodes.length) {
      setRecoveryCodes(r.recoveryCodes);
    }
  };

  const startDisable = () => {
    setDisableForm({ password: '', code: '' });
    setErr(null); setOk(null);
  };

  const cancelDisable = () => {
    setDisableForm(null);
    setErr(null);
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setErr(null); setOk(null);
    if (!disableForm.password || disableForm.code.length !== 6) {
      setErr('Password and a current 6-digit code are both required.');
      return;
    }
    setBusy(true);
    const r = await disableMfa(disableForm.password, disableForm.code);
    setBusy(false);
    if (!r.success) { setErr(r.error); return; }
    setDisableForm(null);
    setOk('Two-factor authentication has been disabled.');
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
      <div className="flex items-start justify-between mb-1">
        <h2 className="text-lg font-medium text-gray-100">Two-factor authentication</h2>
        <span className={`text-xs px-2 py-1 rounded ${enabled ? 'bg-green-900/50 text-green-300 border border-green-800' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>
          {enabled ? 'Enabled' : 'Not enabled'}
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Use an authenticator app (Authy, Google Authenticator, 1Password) to add a second
        factor to your sign-in. Codes refresh every 30 seconds.
      </p>

      {err && (
        <div className="bg-red-900/40 border border-red-800 text-red-200 text-sm rounded px-3 py-2 mb-4">
          {err}
        </div>
      )}
      {ok && (
        <div className="bg-green-900/40 border border-green-800 text-green-200 text-sm rounded px-3 py-2 mb-4">
          {ok}
        </div>
      )}

      {/* State A: not enrolled, no enrolment in progress */}
      {!enabled && !enrolment && !disableForm && (
        <button
          type="button"
          onClick={startEnrol}
          disabled={busy}
          className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 text-white font-medium rounded px-4 py-2 transition-colors"
        >
          {busy ? 'Starting…' : 'Enable two-factor authentication'}
        </button>
      )}

      {/* State B: enrolment in progress — show QR + secret + verification input */}
      {enrolment && (
        <div className="space-y-5">
          <div>
            <div className="text-sm text-gray-300 mb-2">
              <span className="font-medium">Step 1.</span> Scan the QR code with your
              authenticator app, or paste the secret manually.
            </div>
            <div className="flex gap-4 items-start">
              <div className="bg-white p-3 rounded">
                <QRCodeSVG value={enrolment.provisioningUrl} size={160} />
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-400 mb-1">Manual entry secret</div>
                <code className="block bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 font-mono text-sm break-all">
                  {enrolment.secret}
                </code>
                <div className="text-xs text-gray-500 mt-2">
                  Issuer: AI Sentry · Account: {user?.username}
                </div>
              </div>
            </div>
          </div>
          <form onSubmit={submitVerify}>
            <div className="text-sm text-gray-300 mb-2">
              <span className="font-medium">Step 2.</span> Enter the current 6-digit code
              shown by your authenticator to confirm.
            </div>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-3 text-center text-xl tracking-[0.4em] font-mono text-gray-100 focus:outline-none focus:border-blue-500"
              placeholder="000000"
              disabled={busy}
              autoFocus
            />
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={cancelEnrol}
                disabled={busy}
                className="flex-1 border border-gray-700 hover:bg-gray-800 text-gray-300 font-medium rounded px-4 py-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || verifyCode.length !== 6}
                className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-400 text-white font-medium rounded px-4 py-2 transition-colors"
              >
                {busy ? 'Verifying…' : 'Confirm and enable'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Show-once recovery codes — appears after enable or regenerate */}
      {recoveryCodes && (
        <div className="mb-4">
          <RecoveryCodesPanel codes={recoveryCodes} onAcknowledge={() => setRecoveryCodes(null)} />
        </div>
      )}

      {/* State C: enrolled, no disable / regen in progress */}
      {enabled && !disableForm && !regenForm && !recoveryCodes && (
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={startRegen}
            disabled={busy}
            className="border border-gray-700 hover:bg-gray-800 text-gray-200 disabled:opacity-50 font-medium rounded px-4 py-2 transition-colors"
          >
            Regenerate recovery codes
          </button>
          <button
            type="button"
            onClick={startDisable}
            disabled={busy}
            className="border border-red-800 text-red-300 hover:bg-red-900/30 disabled:opacity-50 font-medium rounded px-4 py-2 transition-colors"
          >
            Disable two-factor authentication
          </button>
        </div>
      )}

      {/* Regenerate form — same shape as disable form */}
      {regenForm && (
        <form onSubmit={submitRegen} className="space-y-4">
          <p className="text-sm text-gray-400">
            Issue a new set of recovery codes. The old codes will stop working immediately.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={regenForm.password}
              onChange={(e) => setRegenForm((s) => ({ ...s, password: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={busy}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Authenticator code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={regenForm.code}
              onChange={(e) => setRegenForm((s) => ({ ...s, code: e.target.value.replace(/\D/g, '') }))}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 font-mono tracking-widest text-center focus:outline-none focus:border-blue-500"
              disabled={busy}
              placeholder="000000"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={cancelRegen}
              disabled={busy}
              className="flex-1 border border-gray-700 hover:bg-gray-800 text-gray-300 font-medium rounded px-4 py-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !regenForm.password || regenForm.code.length !== 6}
              className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-400 text-white font-medium rounded px-4 py-2 transition-colors"
            >
              {busy ? 'Generating…' : 'Generate new codes'}
            </button>
          </div>
        </form>
      )}

      {/* State D: disable form — requires password + current TOTP */}
      {disableForm && (
        <form onSubmit={submitDisable} className="space-y-4">
          <p className="text-sm text-gray-400">
            Disabling MFA requires your password and a current authenticator code.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={disableForm.password}
              onChange={(e) => setDisableForm((s) => ({ ...s, password: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={busy}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Authenticator code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={disableForm.code}
              onChange={(e) => setDisableForm((s) => ({ ...s, code: e.target.value.replace(/\D/g, '') }))}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 font-mono tracking-widest text-center focus:outline-none focus:border-blue-500"
              disabled={busy}
              placeholder="000000"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={cancelDisable}
              disabled={busy}
              className="flex-1 border border-gray-700 hover:bg-gray-800 text-gray-300 font-medium rounded px-4 py-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !disableForm.password || disableForm.code.length !== 6}
              className="flex-1 bg-red-700 hover:bg-red-600 disabled:bg-gray-700 disabled:text-gray-400 text-white font-medium rounded px-4 py-2 transition-colors"
            >
              {busy ? 'Disabling…' : 'Disable MFA'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Account;
